package terraultima

import kotlin.math.abs
import kotlin.random.Random

/**
 * What the colony KNOWS about the planet, parameter by parameter.
 *
 * Two design commitments, both from the designer's brief:
 *
 * 1. **The shape of what is knowable is itself public.** A parameter you have not measured reads
 *    "unknown" and says what would have told you. Uncertainty the player can *price* is interesting;
 *    uncertainty that is merely withheld is a cheat. This is Principle A4.1 — model the opacity as
 *    known imprecision, not as information we hold back.
 * 2. **Some things are known from Earth.** Anything astronomy can see at range — size, day length,
 *    how much light it gets, roughly how warm — is known before launch for an in-system target, and
 *    partly known even for a distant one. You do not need a probe to know Mars is cold.
 *
 * The point is that a second playthrough can act on the first: buy the probe, or build something
 * that does not care what the fauna turns out to be.
 */
enum class Confidence { UNKNOWN, ESTIMATED, MEASURED }

data class Fact(
    val key: String,
    val label: String,
    /** One word, for a compact readout. */
    val shortLabel: String,
    val confidence: Confidence,
    /** Null when UNKNOWN. For ESTIMATED this is a noisy value, not the truth. */
    val value: Double?,
    /** Plus-or-minus, for ESTIMATED. */
    val margin: Double,
    val unit: String,
    /** What would tell you, if you do not know. Empty once MEASURED. */
    val learnedBy: String,
    /**
     * WHAT THE NUMBER MEANS FOR PEOPLE LIVING THERE. Empty while UNKNOWN.
     *
     * `gravity 0.38 g · day 25 h · sunlight 0.43 x Earth` is eleven parameters of physics on one
     * line, and a player asked to commit a century to a place cannot read it: 0.43 x Earth is not
     * a fact about anyone's life until somebody says it means the crops need lamps. The number
     * stays — it is what an option's gate is written against — and this is what it is FOR.
     */
    val meaning: String
)

object Knowledge {

    /** Parameters, in the order a player cares about them. */
    private val ORDER = listOf(
        "gravity", "dayLength", "insolation", "temp", "pressure",
        "water", "ore", "toxicity", "seismic", "flora", "fauna"
    )

    private val LABEL = mapOf(
        "gravity" to "surface gravity", "dayLength" to "day length", "insolation" to "sunlight",
        "temp" to "mean temperature", "pressure" to "air pressure", "water" to "accessible water",
        "toxicity" to "soil & air toxicity", "seismic" to "seismic activity",
        "flora" to "plant life", "fauna" to "animal life", "ore" to "metal in the ground"
    )

    /**
     * One word for the compact panel. Chopping the long labels at the last space produced "length"
     * for day length, "ground" for metal in the ground, "activity" for seismic activity, and "life"
     * for BOTH plant and animal life — two identical entries on screen.
     */
    private val SHORT = mapOf(
        "gravity" to "gravity", "dayLength" to "day", "insolation" to "sunlight",
        "temp" to "temperature", "pressure" to "air", "water" to "water",
        "toxicity" to "toxicity", "seismic" to "quakes", "flora" to "plants",
        "fauna" to "animals", "ore" to "metal"
    )

    private val UNIT = mapOf(
        "gravity" to "g", "dayLength" to "h", "insolation" to "x Earth", "temp" to "°C",
        "pressure" to "bar", "water" to "%", "toxicity" to "%", "seismic" to "%",
        "flora" to "%", "fauna" to "%", "ore" to "%"
    )

    /** What instrument or act would settle this parameter. Shown when it is not yet known. */
    private val LEARNED_BY = mapOf(
        "pressure" to "an atmospheric probe, or arriving",
        "temp" to "a survey telescope, or arriving",
        "water" to "a telescope at range; a drone or rover to be sure",
        "toxicity" to "an atmospheric probe, or setting foot on it",
        "seismic" to "a drone, or living there a while",
        "flora" to "a drone over the surface",
        "fauna" to "a rover, or landing and finding out",
        "ore" to "a drone survey, or breaking ground yourself",
        "insolation" to "arriving",
        "gravity" to "arriving",
        "dayLength" to "arriving"
    )

    private fun truth(p: Planet, key: String): Double = when (key) {
        "gravity" -> p.gravity
        "dayLength" -> p.dayLength
        "insolation" -> p.insolation
        "temp" -> p.temp - 273.15
        "pressure" -> p.pressure
        "water" -> p.water * 100
        "toxicity" -> p.toxicity * 100
        "seismic" -> p.seismic * 100
        "flora" -> p.flora * 100
        "fauna" -> p.fauna * 100
        "ore" -> p.ore * 100
        else -> 0.0
    }

    /** How wrong an ESTIMATED reading may be, as a fraction of the plausible range. */
    private fun spread(key: String): Double = when (key) {
        "gravity", "dayLength", "insolation" -> 0.03   // orbital mechanics is not guesswork
        "temp" -> 18.0                                  // degrees; albedo and greenhouse are guesses
        "pressure" -> 0.35
        "water", "toxicity", "seismic", "flora", "fauna", "ore" -> 22.0
        else -> 0.1
    }

    /**
     * The knowledge rules. Deliberately a flat readable table rather than logic scattered through
     * the sim: a player is meant to be able to learn this, so we should be able to read it too.
     */
    fun confidence(s: WorldState, key: String, assumeScanned: Boolean = false): Confidence {
        val landed = s.phase == Phase.SURFACE
        val arrived = landed || s.hasFlag("arrived")
        // An in-system target has had centuries of astronomy, decades of orbiters, and landers.
        // WE ALREADY KNOW MARS' SEISMICITY — InSight measured marsquakes in 2018 — its perchlorates
        // (Phoenix, 2008), its mineralogy from orbital spectroscopy, and that nothing grows there.
        // Treating any of that as "unknown until you fly a drone" is the model insulting the player.
        val nearby = s.build.arrivalYear <= 5
        val caps = Physics.capabilities(s)
        val scanned = assumeScanned || s.hasFlag("planet_scanned")

        if (landed) return Confidence.MEASURED

        // Orbital mechanics is not guesswork. In-system: known exactly, from Earth, before launch.
        if (key in listOf("gravity", "dayLength", "insolation"))
            return if (arrived || nearby) Confidence.MEASURED else Confidence.ESTIMATED

        return when (key) {
            // What an in-system target has already had done to it, by everyone who went before.
            "temp", "pressure", "flora", "fauna" -> if (nearby) Confidence.MEASURED else when (key) {
                // A year in orbit with eyes and optics reads the limb, the clouds and the colour of
                // the ground. Instruments turn that into numbers.
                "temp" -> if (arrived || scanned) Confidence.MEASURED else Confidence.ESTIMATED
                "pressure" -> when {
                    (scanned || arrived) && caps.contains("SCAN_ATMO") -> Confidence.MEASURED
                    scanned || caps.contains("SCAN_DEEP") -> Confidence.ESTIMATED
                    else -> Confidence.UNKNOWN
                }
                "flora" -> when {
                    scanned && caps.contains("DRONE") -> Confidence.MEASURED
                    scanned -> Confidence.ESTIMATED        // you can see green from orbit
                    else -> Confidence.UNKNOWN
                }
                else -> when {                             // fauna: nothing at range finds animals
                    scanned && caps.contains("ROVER") -> Confidence.ESTIMATED
                    else -> Confidence.UNKNOWN
                }
            }
            // Local facts. An in-system target is characterised in general and unknown in detail:
            // ice is mapped from orbit but "accessible water at this site" is a ground question.
            "water" -> when {
                scanned && (caps.contains("DRONE") || caps.contains("ROVER")) -> Confidence.MEASURED
                nearby || scanned || caps.contains("SCAN_DEEP") -> Confidence.ESTIMATED
                else -> Confidence.UNKNOWN
            }
            "toxicity" -> when {
                (scanned || arrived) && caps.contains("SCAN_ATMO") -> Confidence.MEASURED
                nearby -> Confidence.ESTIMATED
                else -> Confidence.UNKNOWN
            }
            "ore" -> when {
                scanned && caps.contains("DRONE") -> Confidence.MEASURED
                nearby || caps.contains("SCAN_DEEP") -> Confidence.ESTIMATED
                else -> Confidence.UNKNOWN
            }
            "seismic" -> when {
                scanned && caps.contains("DRONE") -> Confidence.MEASURED
                nearby -> Confidence.ESTIMATED             // seismometers have been there
                else -> Confidence.UNKNOWN
            }
            else -> Confidence.UNKNOWN
        }
    }

    /**
     * What a year in orbit would actually settle, for THIS expedition — the labels of the
     * parameters whose confidence would improve.
     *
     * It exists because "Hold in orbit and survey it properly" cost forty person-months of food,
     * twenty points of dissent and seven of standing, and on a build carrying no instruments it
     * taught NOTHING: every branch of the table that `planet_scanned` unlocks needs SCAN_ATMO, a
     * DRONE or a ROVER. A cost with an invisible and sometimes absent benefit is the trap the
     * principles call an unmotivated option (A2), and no bot could ever see it.
     */
    fun wouldLearn(s: WorldState): List<String> = ORDER.filter {
        confidence(s, it, assumeScanned = true).ordinal > confidence(s, it).ordinal
    }.map { LABEL[it] ?: it }

    /**
     * The number, in the register of a place people have to live in.
     *
     * Deliberately a flat table of thresholds, like [confidence] above and for the same reason: a
     * player is meant to learn what a thin atmosphere costs them, so it should be readable here.
     * Written against the value the colony BELIEVES — an estimate glosses its own estimate, which
     * is the honest thing to do and occasionally the expensive one.
     */
    fun meaning(key: String, v: Double): String = when (key) {
        "gravity" -> when {
            v < 0.15 -> "barely holds anything down; bones and dust both go where they like"
            v < 0.45 -> "about a third of Earth's pull, so everything is light and nothing settles"
            v < 0.80 -> "noticeably light, and a body can live in it"
            v < 1.25 -> "near enough to Earth's that nobody would remark on it"
            else -> "heavier than Earth, in every load and every step and every heart"
        }
        "dayLength" -> when {
            v < 6 -> "the sun crosses in a few hours; there is no rhythm to keep"
            v < 20 -> "a short day, and a body adjusts"
            v < 28 -> "an Earth-like day, the one thing here nobody has to relearn"
            v < 200 -> "light and dark arrive in shifts, not nights"
            else -> "weeks of day and weeks of night: light is a season here"
        }
        "insolation" -> when {
            v < 0.25 -> "a twilight world: nothing grows without lamps, and lamps need power"
            v < 0.60 -> "dim: crops want lamps, and panels give back less than they promise"
            v < 1.30 -> "enough light to grow by"
            else -> "more light than Earth gets, with the heat and the radiation of it"
        }
        "temp" -> when {
            v < -80 -> "colder than anywhere on Earth, permanently"
            v < -20 -> "polar cold, every day of the year"
            v < 5 -> "cold, and workable in the right clothes"
            v < 30 -> "temperate: a coat and a hat sort of world"
            else -> "hotter than any desert on Earth"
        }
        "pressure" -> when {
            v < 0.01 -> "effectively vacuum: a hull or a suit, always, for everyone"
            v < 0.30 -> "far too thin to breathe, thick enough to carry dust and weather"
            v < 0.70 -> "thin: breathable only with help, and it never gets easier"
            v < 1.50 -> "pressure a lung can work with, whatever is mixed into it"
            else -> "thicker than Earth's air, and it presses"
        }
        "water" -> when {
            v < 5 -> "next to none within reach, so the loop is all there is"
            v < 25 -> "some, and you would be hauling every litre of it"
            v < 60 -> "enough to build a settlement around"
            else -> "water is not the thing that will kill you here"
        }
        "toxicity" -> when {
            v < 10 -> "the ground and the air are inert"
            v < 40 -> "dust that has to be kept out of things, and mostly can be"
            else -> "the soil and the air will hurt whoever works them, slowly"
        }
        "seismic" -> when {
            v < 10 -> "the ground is quiet"
            v < 40 -> "it moves, now and then, and you would build for it"
            else -> "it shakes: nothing built rigid stays standing for long"
        }
        "flora" -> when {
            v < 5 -> "nothing grows here, and nothing ever has"
            v < 30 -> "something grows, sparse, and nobody knows what it wants"
            else -> "a living biosphere, which is a gift and a question"
        }
        "fauna" -> when {
            v < 5 -> "nothing lives here"
            v < 30 -> "something lives here, and has not been met"
            else -> "animals: you are arriving somewhere already occupied"
        }
        "ore" -> when {
            v < 10 -> "little metal near the surface, so what you brought is what you have"
            v < 40 -> "workable metal, once there is something to work it with"
            else -> "rich ground: the ridges are full of it"
        }
        else -> ""
    }

    fun facts(s: WorldState): List<Fact> = ORDER.map { key ->
        val c = confidence(s, key)
        val t = truth(s.planet, key)
        val noise = s.estimateNoise[key] ?: 0.0
        Fact(
            key = key,
            label = LABEL[key] ?: key,
            shortLabel = SHORT[key] ?: key,
            confidence = c,
            value = when (c) {
                Confidence.UNKNOWN -> null
                Confidence.MEASURED -> t
                Confidence.ESTIMATED -> t + noise * spread(key)
            },
            margin = if (c == Confidence.ESTIMATED) spread(key) else 0.0,
            unit = UNIT[key] ?: "",
            learnedBy = if (c == Confidence.MEASURED) "" else (LEARNED_BY[key] ?: ""),
            meaning = if (c == Confidence.UNKNOWN) "" else meaning(key, when (c) {
                Confidence.MEASURED -> t
                else -> t + noise * spread(key)
            })
        )
    }

    /**
     * Fixed at expedition start so an estimate does not jitter every time the player looks at it.
     * A wrong estimate must stay wrong in the same direction — that is what makes it a risk the
     * player carries rather than a dice roll they can re-read.
     */
    fun seedEstimates(s: WorldState, rng: Random) {
        ORDER.forEach { s.estimateNoise[it] = rng.nextDouble(-1.0, 1.0) }
    }
}
